import React from 'react';
import { useIntl, defineMessages } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { IconButton } from '@lifeomic/chroma-react/components/IconButton';
import { Tooltip } from '@lifeomic/chroma-react/components/Tooltip';
import { Send } from '@lifeomic/chromicons';
import { consentActions } from '@lifeomic/phc-web-toolkit/src/redux/modules/consent';
import { canReassignMemberConsent } from '@lifeomic/phc-web-toolkit/src/util/abac';
import { evaluatedPolicySelectors } from '@lifeomic/phc-web-toolkit/src/redux/modules/evaluatedPolicy';
import { projectPickerSelectors } from '@lifeomic/phc-web-toolkit/src/redux/modules/projectPicker';
import { projectCohortPickerSelectors } from '@lifeomic/phc-web-toolkit/src/redux/modules/projectCohortPicker';
import { bindContextKeyToSelectors } from '@lifeomic/phc-web-toolkit/src/redux/modules/asyncRequestContexts/selectors';
import NotificationHelper from '@lifeomic/phc-web-toolkit/src/redux/util/NotificationHelper';
import { contextKeys } from '../../../../../redux/modules/asyncRequestContexts';
import { notificationsActions } from '@lifeomic/phc-web-toolkit/src/redux/modules/notifications';

const messages = defineMessages({
  sendTooltip: {
    id: 'patientViewer.subjectConsent.reassignRowTooltip',
    defaultMessage: 'Re-assign consent to subject',
  },
  inviteLabel: {
    id: 'patientViewer.subjectConsent.reassignRowAria',
    defaultMessage: 'Re-assign consent to subject',
  },
  success: {
    id: 'patientViewer.subjectConsent.reassignSuccess',
    defaultMessage: 'Consents were updated. The list will refresh.',
  },
  error: {
    id: 'patientViewer.subjectConsent.reassignError',
    defaultMessage: 'Re-assign failed. Please try again or contact support.',
  },
});

type ConsentRow = {
  id?: string;
  name?: string | null;
  /** Set from `meta.tag` or `active.questionnaire` in `parseConsent` */
  questionnaire?: string | null;
  status?: string;
};

type Props = {
  patient: { id?: string } | null;
  listContextKey: string;
  consent: ConsentRow;
};

export const ReassignMemberConsentRowButton: React.FC<Props> = props => {
  const { formatMessage } = useIntl();
  const dispatch = useDispatch();
  const policy = useSelector(evaluatedPolicySelectors.selectPolicyForUser);
  const projectId = useSelector(projectPickerSelectors.selectActiveProjectId);
  const cohortId = useSelector(
    projectCohortPickerSelectors.selectActiveProjectCohortId,
  );

  const directiveId = props.consent?.id;
  const consentName = props.consent?.name;
  const questionnaireId = props.consent?.questionnaire ?? undefined;

  const isFetchingSelector = React.useMemo(() => {
    if (!directiveId) {
      return () => false;
    }
    return bindContextKeyToSelectors(
      contextKeys.patientViewer.reassignCreatePatientConsentDirective(
        directiveId,
      ),
    ).isFetchingSelector;
  }, [directiveId]);

  const isSubmitting = useSelector(isFetchingSelector);

  const allowed = canReassignMemberConsent(
    policy,
    projectId ?? undefined,
    cohortId,
    props.patient?.id,
  );

  const patientId = props.patient?.id;

  const assignable = props.consent?.status !== 'proposed';

  if (
    !allowed ||
    !patientId ||
    !directiveId ||
    !consentName ||
    !assignable ||
    !questionnaireId
  ) {
    return null;
  }

  const handleClick = async (e: React.MouseEvent) => {
    e.stopPropagation();
    (e.currentTarget as HTMLButtonElement).blur();

    const notification = new NotificationHelper(
      formatMessage(messages.success),
      formatMessage(messages.error),
    );

    try {
      await (dispatch as any)(
        consentActions.reassignMemberConsents({
          patientId,
          listContextKey: props.listContextKey,
          formsContextKey:
            contextKeys.patientViewer.reassignFetchConsentForms(directiveId),
          checkDirectivesContextKey:
            contextKeys.patientViewer.reassignCheckPatientDirectives(
              directiveId,
            ),
          createDirectiveContextKey:
            contextKeys.patientViewer.reassignCreatePatientConsentDirective(
              directiveId,
            ),
          questionnaireId,
          reassignActiveConsents: true,
          notification,
        }),
      );
    } catch (err) {
      dispatch(
        notificationsActions.addNotification(
          formatMessage(messages.error) +
            (err instanceof Error ? ` ${err.message}` : ''),
          { statusType: 'error' },
        ),
      );
    }
  };

  return (
    <Tooltip title={formatMessage(messages.sendTooltip)}>
      <IconButton
        aria-label={formatMessage(messages.inviteLabel)}
        color="inverse"
        onClick={handleClick}
        icon={Send}
        disabled={isSubmitting}
      />
    </Tooltip>
  );
};
